import './Compensation.scss';
import { Dispatch, SetStateAction } from 'react';
import { Field } from '../../models/home';
import { handleOnChangeSelect, handleOnChangeText } from '../../helpers/form';
import { NUM_OF_COMPENSATIONS } from '../../constants/dropdown';
import { useCompensation } from './useCompensation';
import { useMasterData } from '../../providers/MasterDataProvider';
import AppSelect from '../AppCommon/AppSelect/AppSelect';
import AppText from '../AppCommon/AppText/AppText';
import { SelectChangeEvent } from '@mui/material';
import { useMemo } from 'react';
import { SEAT_RELATED_CATEGORY_IDS, CABIN_IDS } from '../../constants/authGroups';

interface Props {
    fields: Field[];
    setFields: Dispatch<SetStateAction<Field[]>>;
}

const Compensation = ({fields, setFields}: Props) => {
    const { masterData } = useMasterData();
    const {
        reasonOptions,
        categoryOptions,
        cabinOptions,
        compensationTypes,
        selectedReasonId,
        selectedCategoryId,
        selectedReason,
        selectedCategory,
        selectedCabin,
        selectedReasonDisplayText,
        selectedCategoryDisplayText,
        selectedCabinDisplayText,
        shouldShowCabin,
        showOptionalCommentsLabel,
        WIFI_REGIONS,
        setShowOptionalCommentsLabel,
        t
    } = useCompensation({fields, setFields});

    const handleReasonChange = (event: SelectChangeEvent<string>) => {
        const selectedReason = reasonOptions.find(r => r.value === event.target.value);
        setFields(prevFields => {
            return prevFields.map(f => {
                if (f.name === 'reasonId') {
                    return { ...f, value: selectedReason?.id || '' };
                }
                if (f.name === 'categoryId') {
                    return { ...f, value: '' };
                }
                if (f.name === 'cabinId') {
                    return { ...f, value: '' };
                }
                if (f.name === 'compensationType') {
                    return { ...f, value: '' };
                }
                return f;
            });
        });
    };

    const handleCategoryChange = (event: SelectChangeEvent<string>) => {
        const selectedCategory = categoryOptions.find(c => c.value === event.target.value);
        setFields(prevFields => {
            return prevFields.map(f => {
                if (f.name === 'categoryId') {
                    return { ...f, value: selectedCategory?.id || '' };
                }
                if (f.name === 'cabinId') {
                    return { ...f, value: '' };
                }
                if (f.name === 'compensationType') {
                    return { ...f, value: '' };
                }
                return f;
            });
        });
    };

    // Check if we should show the customer care notification
    const shouldShowCustomerCareNotification = useMemo(() => {
        if (!selectedReason || !selectedCategory || !selectedCabin) return false;

        // Get the selected category from master data to check its properties
        const categoryFromMaster = masterData.categories.find(cat => cat.categoryId === selectedCategory.categoryId);
        if (!categoryFromMaster) return false;

        const isSeatRelated = selectedReason.reasonId === categoryFromMaster.reasonId;
        
        // Check if the category ID matches any of the seat-related issue IDs
        const isSeatRelatedIssue = 
            categoryFromMaster.categoryId === SEAT_RELATED_CATEGORY_IDS.SEAT_DOES_NOT_RECLINE ||
            categoryFromMaster.categoryId === SEAT_RELATED_CATEGORY_IDS.SEAT_DEFLATED ||
            categoryFromMaster.categoryId === SEAT_RELATED_CATEGORY_IDS.TABLE_TRAY_BROKEN ||
            categoryFromMaster.categoryId === SEAT_RELATED_CATEGORY_IDS.FOOT_REST_BROKEN;
        
        // Check if the selected cabin ID matches the Signature class cabin ID
        const isSignatureClass = selectedCabin.cabinId === CABIN_IDS.SIGNATURE_CLASS;
        
        return isSeatRelated && isSeatRelatedIssue && isSignatureClass;
    }, [selectedReason, selectedCategory, selectedCabin, selectedReasonDisplayText, selectedCategoryDisplayText, selectedCabinDisplayText, masterData]);

    const handleCabinChange = (event: SelectChangeEvent<string>) => {
        const target = (event.target.value || '').trim().toLowerCase();
        const selectedCabin = cabinOptions.find(c => (c.value || '').trim().toLowerCase() === target);
        setFields(prevFields => {
            return prevFields.map(f => {
                if (f.name === 'cabinId') {
                    return { ...f, value: selectedCabin?.id || '' };
                }
                if (f.name === 'compensationType') {
                    return { ...f, value: '' };
                }
                return f;
            });
        });
    };

    const reasonField = fields.find(f => f.name === 'reasonId') || fields[0];
    const categoryField = fields.find(f => f.name === 'categoryId') || fields[1];
    const cabinField = fields.find(f => f.name === 'cabinId') || fields[2];
    const compensationNumField = fields.find(f => f.name === 'compensationNum') || fields[3];
    const commentsField = fields.find(f => f.name === 'comments') || fields[4];
    const compensationTypeField = fields.find(f => f.name === 'compensationType') || fields[5];
    const wifiRegionField = fields.find(f => f.name === 'wifiRegion') || fields[6];

    return (
        <div className="c-compensation">
            <div className="c-row">
                {/* Left Column - Reason, Category, Cabin */}
                <div className="c-col" style={{ width: '50%' }}>
                    {/* Reason for Compensation */}
                    <div className="c-row">
                        <div className="c-col" style={{ width: '100%' }}>
                            <AppSelect
                                itemList={reasonOptions.map(r => r.value)}
                                label={t('labels.compensationReason')}
                                value={selectedReasonDisplayText}
                                width={100}
                                disabled={reasonField.disabled}
                                error={!!reasonField.requiredError}
                                helperText={reasonField.requiredError}
                                onChange={handleReasonChange}
                            />
                        </div>
                    </div>

                    {/* Category */}
                    {selectedReasonId && categoryOptions.length > 0 && (
                        <div className="c-row" style={{ marginTop: '1.6rem' }}>
                            <div className="c-col" style={{ width: '100%' }}>
                                <AppSelect
                                    itemList={categoryOptions.map(c => c.value)}
                                    label={t('labels.compensationCategory') || 'Category'}
                                    value={selectedCategoryDisplayText}
                                    width={100}
                                    disabled={categoryField.disabled}
                                    error={!!categoryField.requiredError}
                                    helperText={categoryField.requiredError}
                                    onChange={handleCategoryChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* Cabin */}
                    {selectedReasonId && shouldShowCabin && (selectedCategoryId || categoryOptions.length === 0) && (
                        <div className="c-row" style={{ marginTop: '1.6rem' }}>
                            <div className="c-col" style={{ width: '100%' }}>
                                <AppSelect
                                    itemList={cabinOptions.map(c => c.value)}
                                    label={t('labels.compensationCabin') || 'Cabin'}
                                    value={selectedCabinDisplayText}
                                    width={100}
                                    disabled={cabinField.disabled}
                                    error={!!cabinField.requiredError}
                                    helperText={cabinField.requiredError}
                                    onChange={handleCabinChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* Additional Details */}
                    <div className="c-row" style={{ marginTop: '1.6rem' }}>
                        <div className="c-col" style={{ width: '100%' }}>
                            <AppText
                                label={!commentsField.value && showOptionalCommentsLabel ? t('labels.optionalComments') : ''}
                                width={100}
                                disabled={commentsField.disabled}
                                error={!!commentsField.requiredError}
                                height={12.7}
                                helperText={commentsField.requiredError}
                                maxLength={250}
                                multiLine
                                value={commentsField.value}
                                onBlur={e => setShowOptionalCommentsLabel(e.target.value ? false : true)}
                                onChange={e => {
                                    const index = fields.findIndex(f => f.name === 'comments');
                                    handleOnChangeText(e, index >= 0 ? index : 4, setFields);
                                }}
                                onFocus={() => setShowOptionalCommentsLabel(false)}
                            />
                        </div>
                    </div>
                </div>

                {/* Right Column - Compensation Type and WiFi Region */}
                <div className="c-col" style={{ width: '50%' }}>
                    {/* Number of Compensations */}
                    <div className="c-row">
                        <div className="c-col c-col--with-notes" style={{ width: '100%' }}>
                            <AppSelect
                                itemList={NUM_OF_COMPENSATIONS}     
                                label={t('labels.compensationNum')}
                                value={compensationNumField.value}
                                width={100}
                                disabled={compensationNumField.disabled}
                                error={!!compensationNumField.requiredError}
                                helperText={compensationNumField.requiredError}
                                onChange={e => {
                                    const index = fields.findIndex(f => f.name === 'compensationNum');
                                    handleOnChangeSelect(e, index >= 0 ? index : 3, setFields);
                                }}
                            />
                            <p className="c-notes" dangerouslySetInnerHTML={{ __html: t('labels.notesReason') }}></p>
                        </div>
                    </div>

                    {/* Compensation Type */}
                    {selectedReasonId && compensationTypes.length > 0 && (
                        <div className="c-row c-row--compensation-type" style={{ margin: '98px 0 0 0' }}>
                            <div className="c-col" style={{ width: '100%' }}>
                                <AppSelect
                                    itemList={compensationTypes}
                                    label={t('labels.compensationType')}
                                    value={compensationTypeField.value}
                                    width={100}
                                    disabled={compensationTypeField.disabled}
                                    error={!!compensationTypeField.requiredError}
                                    helperText={compensationTypeField.requiredError || 
                                        (compensationTypeField.value && compensationTypeField.value !== 'Wi-Fi' ? t('labels.ntpTypeHint') : undefined)}
                                    onChange={e => {
                                        const index = fields.findIndex(f => f.name === 'compensationType');
                                        handleOnChangeSelect(e, index >= 0 ? index : 5, setFields);
                                    }}
                                />
                                {compensationTypeField.value === t(`compensationTypes.wifi`) && (
                                    <div className="c-col c-col--wifi-region" style={{ marginTop: '0.5rem' }}>
                                        <AppSelect
                                            itemList={WIFI_REGIONS}
                                            label={t('labels.wifiRegion')}
                                            value={wifiRegionField.value}
                                            width={200}
                                            disabled={wifiRegionField.disabled}
                                            error={!!wifiRegionField.requiredError}
                                            helperText={wifiRegionField.requiredError || t('labels.wifiRegionHint')}
                                            onChange={e => {
                                                const index = fields.findIndex(f => f.name === 'wifiRegion');
                                                handleOnChangeSelect(e, index >= 0 ? index : 6, setFields);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {shouldShowCustomerCareNotification && (
                        <p 
                            className="c-notes c-notes--customer-care" 
                             style={{ marginTop: '70px' }}
                             dangerouslySetInnerHTML={{ __html: t('labels.customerCareNotification') }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Compensation;